import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { withRouter } from 'react-router';
import queryString from 'query-string';

import Localizable from 'lib/intl/localizable';

import { Form, Field } from 'lib/form/form';
import { ArrayOfFieldData } from 'lib/form/array-of-field-data';
import withDialog from 'lib/alerts/with-dialog';
import { RetryDialog, InfoDialog } from 'lib/alerts/dialog';
import Styled from 'styled';
import SVGIconView from 'lib/images/svg-icon-view';

import { UserAvatarViewModel } from 'four-nets/user/user-avatar/user-avatar-view';
import FieldWithLabelView from 'four-nets/ui/form/field-with-label-view';
import TextInputView from 'four-nets/ui/text-input-view';
import FileThumbnailView from 'four-nets/ui/file-thumbnail-view';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import SubmitButtonView from 'four-nets/ui/submit-button/submit-button-view';
import newArticle from 'four-nets/icons/svg/icon_new-article.svg';
import TextAreaView from 'four-nets/ui-v2/form/text-area/text-area';
import PhotoFileInputView from 'four-nets/photofile/form/photofile-input-view';
import FileUploadListView from 'four-nets/ui/form/file-upload-list-view';
import withPhotofiles from 'four-nets/photofile/form/with-photofiles';
import NewCommentView from 'four-nets/common/comment/new-comment-view';

import defaultStyles from './new-message-view.scss';

@withPhotofiles({ type: 5, fieldName: 'images' })
class NewMessageForm extends Form {}

@Localizable()
@withDialog
@Styled(defaultStyles)
class NewMessageView extends React.Component {
    static getDerivedStateFromProps(props, state) {
        if (props.buyer && !props.isAuthor && !state.email && !state.name) {
            return {
                email: props.buyer.email,
                name: props.buyer.name,
            };
        }
        return {};
    }

    textArea: ?TextAreaView;

    photoFileInput: ?PhotoFileInputView;

    constructor(props) {
        super(props);
        const { product } = props;
        this.state = {
            form: new NewMessageForm({
                photoFileStore: product && product.photoFileStore,
                field: { images: new Field({ data: new ArrayOfFieldData() }) },
            }),
            email: '',
            name: '',
            text: '',
            submitting: false,
        };
    }

    submit = () => {
        this.setState({ submitting: true });

        const { buyer, product, isAuthor } = this.props;
        const { form, text, name, email } = this.state;
        const images = form.field.get('images').data.submitData();

        product
            .createMessage(
                text,
                name,
                email,
                images.map(({ id }) => id),
                buyer
            )
            .then(() => {
                form.submitting = false;
                form.submitted = true;
                form.reset();

                this.setState({ text: '' })

                const { location, showDialog, forceUpdate } = this.props;
                if (!product.userStore.me && !queryString.parse(location.search).token) {
                    const { intl } = this.props;
                    showDialog(
                        new InfoDialog({
                            onOK: forceUpdate,
                            message: intl.formatMessage(
                                defineMessages({
                                    BAZAAR_WILL_SEND_CONFIRMATION: { id: 'BAZAAR_WILL_SEND_CONFIRMATION' },
                                }).BAZAAR_WILL_SEND_CONFIRMATION
                            ),
                            title: intl.formatMessage(
                                defineMessages({ BAZAAR_INTEREST: { id: 'BAZAAR_INTEREST' } }).BAZAAR_INTEREST
                            ),
                        })
                    );
                } else {
                    forceUpdate();
                }
            })
            .catch(err => {
                const { showDialog } = this.props;

                showDialog(
                    new RetryDialog({
                        retry: this.submit,
                        message: err.message,
                    })
                );
            })
            .finally(() => {
                this.setState({ submitting: false });
            });
    };

    setPhotoFileInput = ref => {
        this.photoFileInput = ref;
    };

    errors = () => {
        const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        const errors = [];
        const {
            product: {
                userStore: { me },
            },
        } = this.props;
        const { name, email, text } = this.state;

        const notLoggedInAndNoCredentials = !me && (!name || !email);
        if (notLoggedInAndNoCredentials || !text) {
            errors.push(<FormattedMessage id='FORM_REQUIRED_VALIDATOR_ERROR' />);
        }

        if (!me && !EMAIL_REGEX.test(email)) {
            errors.push(<FormattedMessage id='FORM_INVALID_EMAIL_VALIDATOR_ERROR' />);
        }

        return errors.length > 0 ? errors : null;
    };

    render() {
        const { product } = this.props;
        
        if (!product || product.isHidden) {
            return null;
        }

        const { submitting, form } = this.state;
        const { text, name, email } = this.state;
        const { isAuthor, buyer, involved, styles, disabled: propsDisabled } = this.props;
        const {
            userStore: { me },
            user,
        } = product;

        const paused = product.status === 1 && !involved;
        const disabled = propsDisabled || (paused && !buyer);

        const fieldDataStub = {
            value: text,
            setValue: ({ value }) => this.setState({ text: value }),
            onBlur: () => null,
            onChange: event => this.setState({ text: event.target.value }),
            reset: () => this.setState({ text: '' }),
        };

        return (
            <NewCommentView
                style={{ newComment: styles.newComment }}
                newCommentViewModel={{ userAvatarViewModel: new UserAvatarViewModel({ user: me }) }}
                title={
                    isAuthor ? (
                        <FormattedMessage id='BAZAAR_MESSAGE_FOR_BUYER' />
                    ) : (
                        <FormattedMessage id='BAZAAR_MESSAGE_FOR_SELLER' />
                    )
                }
            >
                <LoadingWrapperView loading={submitting}>
                    <form className={styles.mainWrapper}>
                        <div className={styles.directionWrapper}>
                            <div className={styles.secondaryWrapper}>
                                <div className={disabled ? styles.disabled : styles.displayNone}>
                                    <FormattedMessage id='BAZAAR_CANT_SEND_MESSAGE' />
                                    &nbsp;
                                    {paused ? (
                                        <FormattedMessage id='BAZAAR_INACTIVE' />
                                    ) : (
                                        <FormattedMessage id='BAZAAR_DELETED' />
                                    )}
                                    .
                                </div>
                                <FormattedMessage id='BAZAAR_YOUR_MESSAGE'>
                                    {msg => (
                                        <label>
                                            <span className={styles.messageLabel}>{msg}</span>
                                            <FormattedMessage id='BAZAAR_CONVERSATION_IS_PRIVATE'>
                                                {message => (
                                                    <TextAreaView
                                                        name='message'
                                                        field={{ placeholder: message, data: fieldDataStub }}
                                                        disabled={disabled}
                                                    />
                                                )}
                                            </FormattedMessage>
                                        </label>
                                    )}
                                </FormattedMessage>
                                {!me ? (
                                    <div className={styles.fieldWrapper}>
                                        <FieldWithLabelView
                                            style={{ filedLabel: styles.fieldLabel }}
                                            label={<FormattedMessage id='BAZAAR_YOUR_EMAIL' />}
                                            field={
                                                <TextInputView
                                                    name='email'
                                                    type='email'
                                                    className={styles.field}
                                                    disabled={disabled || buyer}
                                                    value={email}
                                                    onChange={event =>
                                                        this.setState({
                                                            email: event.target.value,
                                                        })
                                                    }
                                                />
                                            }
                                            mini={true}
                                        />
                                        <FieldWithLabelView
                                            style={{ filedLabel: styles.fieldLabel }}
                                            label={<FormattedMessage id='BAZAAR_NAME_SURNAME' />}
                                            field={
                                                <TextInputView
                                                    name='surname'
                                                    type='text'
                                                    className={styles.field}
                                                    disabled={disabled || buyer}
                                                    value={name}
                                                    onChange={event =>
                                                        this.setState({
                                                            name: event.target.value,
                                                        })
                                                    }
                                                />
                                            }
                                            mini={true}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                        <div className={styles.buttonsWrapper}>
                            <span className={styles.submitButton}>
                                <SubmitButtonView
                                    icon={<SVGIconView src={newArticle} />}
                                    disabled={this.errors()}
                                    onClick={this.submit}
                                >
                                    <FormattedMessage id='BAZAAR_SEND_MESSAGE' />
                                </SubmitButtonView>
                            </span>
                            {me ? (
                                <span>
                                    <PhotoFileInputView
                                        ref={this.setPhotoFileInput}
                                        field={form.field.get('images')}
                                        fieldDataForFiles={form.photoFileFieldDataForFiles}
                                        disabled={disabled && !involved}
                                    />
                                </span>
                            ) : null}
                        </div>
                        <div className={styles.fileUploadWrapper}>
                            <div
                                className={`${styles.fileUpload} ${
                                    form.field.get('images').data.fields.length ? styles.withImages : ''
                                }`}
                            >
                                <FileUploadListView
                                    field={form.field.get('images')}
                                    thumbnail={<FileThumbnailView height={25} width={25} />}
                                />
                            </div>
                        </div>
                    </form>
                </LoadingWrapperView>
            </NewCommentView>
        );
    }
}

export default withRouter(NewMessageView);
